import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { AlertType, TextButton, TextInput, Typography } from '../../../components';
import { useAppDispatch, useHandleApiResponse, useToast } from '../../../hooks';
import { updateOrganization } from '../../../redux/reducers';
import { useSubmitGongAPIKeyMutation } from '../../../services';
import { GongAuth, GongAuthMethod, IntegrationConnectionStatus, IntegrationService, TextColor } from '../../../types';
import { isValidURL } from '../../../utils';

const SECRET_KEY_HIDDEN_CHARS_NUMBER = 42;
const CONNECT_GONG_ERROR_MSG = 'Connection to Gong failed. Please try again later.';

interface ConnectGongApiKeyProps {
  connectionStatus: IntegrationConnectionStatus;
  setConnectionStatus: Dispatch<SetStateAction<IntegrationConnectionStatus>>;
  gongAuth?: GongAuth;
}

const ConnectGongApiKey = ({ connectionStatus, setConnectionStatus, gongAuth }: ConnectGongApiKeyProps) => {
  const [accessKeyInput, setAccessKeyInput] = useState('');
  const [secretKeyInput, setSecretKeyInput] = useState('');
  const [apiBaseUrlInput, setApiBaseUrlInput] = useState('');

  const { secretKeyLast4, accessKey, apiBaseUrl } = gongAuth || {};

  const dispatch = useAppDispatch();

  const [submitGongAPIKey, { isLoading: isSubmitting }] = useSubmitGongAPIKeyMutation();

  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  // Reset the inputs when the connection is disconnected.
  useEffect(() => {
    if (connectionStatus === IntegrationConnectionStatus.DISCONNECTED) {
      setAccessKeyInput('');
      setSecretKeyInput('');
      setApiBaseUrlInput('');
    }
  }, [connectionStatus]);

  useEffect(() => {
    if (secretKeyLast4 && accessKey && apiBaseUrl) {
      setSecretKeyInput('*'.repeat(SECRET_KEY_HIDDEN_CHARS_NUMBER) + secretKeyLast4);
      setAccessKeyInput(accessKey);
      setApiBaseUrlInput(apiBaseUrl);
    }
  }, [secretKeyLast4, accessKey, apiBaseUrl]);

  // Handle the success response
  const onSuccess = useCallback(() => {
    dispatch(
      updateOrganization({
        gongAuth: {
          service: IntegrationService.GONG,
          expiresAt: null,
          authMethod: GongAuthMethod.CLIENT_CREDS,
          accessKey: accessKeyInput,
          secretKeyLast4: secretKeyInput.slice(-4),
          apiBaseUrl: apiBaseUrlInput,
        },
      })
    );
  }, [dispatch, secretKeyInput, accessKeyInput, apiBaseUrlInput]);

  const onError = useCallback(() => {
    setConnectionStatus(IntegrationConnectionStatus.FAILED);
  }, [setConnectionStatus]);

  // Handle the API key submission
  const handleSubmitGongAPIKey = useCallback(async () => {
    // Check URL validation
    if (!isValidURL(apiBaseUrlInput)) {
      showToast({ message: 'Please enter a valid API base URL.', type: AlertType.ERROR });
      return;
    }

    // Reset the connection failed state.
    setConnectionStatus(IntegrationConnectionStatus.DISCONNECTED);
    try {
      const response = await submitGongAPIKey({
        service: IntegrationService.GONG,
        accessKey: accessKeyInput,
        secretKey: secretKeyInput,
        apiBaseUrl: apiBaseUrlInput,
      });
      handleApiResponse({
        response,
        errorMsg: CONNECT_GONG_ERROR_MSG,
        successMsg: 'Successfully connected to Gong',
        onError,
        onSuccess,
      });
    } catch (error) {
      onError();
      showToast({ message: CONNECT_GONG_ERROR_MSG, type: AlertType.ERROR });
    }
  }, [
    submitGongAPIKey,
    handleApiResponse,
    onSuccess,
    onError,
    showToast,
    accessKeyInput,
    secretKeyInput,
    apiBaseUrlInput,
    setConnectionStatus,
  ]);

  const isConnected = connectionStatus === IntegrationConnectionStatus.CONNECTED;
  const hasEmptyFields = !accessKeyInput || !secretKeyInput || !apiBaseUrlInput;

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Typography color={TextColor.SECONDARY}>Username / Access Key</Typography>
          <TextInput
            value={accessKeyInput}
            onChange={(e) => setAccessKeyInput(e.target.value)}
            disabled={isConnected}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Typography color={TextColor.SECONDARY}>Password / Access Key Secret</Typography>
          <TextInput
            value={secretKeyInput}
            onChange={(e) => setSecretKeyInput(e.target.value)}
            disabled={isConnected}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Typography color={TextColor.SECONDARY}>API Base URL</Typography>
          <TextInput
            value={apiBaseUrlInput}
            onChange={(e) => setApiBaseUrlInput(e.target.value)}
            disabled={isConnected}
          />
        </div>
      </div>
      <div className="-mt-2">
        <TextButton
          disabled={isConnected || hasEmptyFields}
          text="Connect"
          onClick={handleSubmitGongAPIKey}
          loading={isSubmitting}
        />
      </div>
    </>
  );
};

export default ConnectGongApiKey;
